import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, formatApiError } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const check = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentDetails(res.data);
        if (res.data.payment_status === "paid") setStatus("success");
        else if (attempts < 5) setTimeout(() => setAttempts(prev => prev + 1), 2000);
        else setStatus("error");
      } catch {
        if (attempts < 5) setTimeout(() => setAttempts(prev => prev + 1), 2000);
        else setStatus("error");
      }
    };
    check();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="payment-success-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <div className="max-w-sm w-full text-center">
        {status === "checking" && (
          <div>
            <Loader2 className="w-12 h-12 mx-auto text-cyan-400/40 animate-spin mb-8" />
            <h1 className="text-2xl font-['Rajdhani'] tracking-wide mb-2">PROCESSING</h1>
            <p className="text-cyan-200/40">Confirming your payment...</p>
          </div>
        )}
        {status === "success" && (
          <div>
            <div className="w-16 h-16 mx-auto border border-green-400/30 rounded-sm flex items-center justify-center mb-8 shadow-[0_0_20px_rgba(0,200,100,0.2)]">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-['Rajdhani'] tracking-wide mb-2">PAYMENT COMPLETE</h1>
            <p className="text-cyan-200/40 mb-8">Your encoding jobs will begin shortly.</p>
            {paymentDetails && (
              <div className="card-glass rounded-sm p-6 mb-8 text-left">
                <div className="flex justify-between text-sm mb-2"><span className="text-cyan-200/40">Amount</span><span className="text-cyan-300">${(paymentDetails.amount_total / 100).toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-cyan-200/40">Status</span><span className="text-green-400">Paid</span></div>
              </div>
            )}
            <Button onClick={() => navigate("/dashboard")} className="h-12 px-8 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm" data-testid="go-to-dashboard">
              DASHBOARD <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
        {status === "error" && (
          <div>
            <div className="w-16 h-16 mx-auto border border-red-400/30 rounded-sm flex items-center justify-center mb-8">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-['Rajdhani'] tracking-wide mb-2">PAYMENT ISSUE</h1>
            <p className="text-cyan-200/40 mb-8">Unable to confirm payment. Contact support if charged.</p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="h-12 px-6 rounded-sm btn-outline-blue font-['Rajdhani'] tracking-wider">Dashboard</Button>
              <Button onClick={() => window.location.reload()} className="h-12 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider">Retry</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
